import { useAuth0 } from "@auth0/auth0-react"
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios"
import { toast } from "react-toastify"
import { getEnv } from "../config"
import { ToasterLogout } from "./ToasterLogout"
import qs from "qs"

export type AxiosRequestConfigWithData<Data> = Omit<
  AxiosRequestConfig,
  "data"
> & {
  data?: Data
}

function createAxiosInstance() {
  const baseURL = (getEnv("REACT_APP_SERVERAPIURL") as string)?.trim()
  const opts = {
    baseURL: baseURL,
    timeout: 1000 * 1000,
    paramsSerializer: (params: any) => qs.stringify(params, { arrayFormat: "repeat" })
    // withCredentials: true,
  }

  return axios.create({
    ...opts,
  })
}

const instance = createAxiosInstance()

export function axiosRequest<Request, Response>(
  options: AxiosRequestConfigWithData<Request>
): AxiosPromise<Response> {
  return instance({
    ...options,
  }) as AxiosPromise<Response>
}

export function useAxiosRequest() {
  const { getAccessTokenSilently, user } = useAuth0()
  return async function <Request, Response>(
    options: AxiosRequestConfigWithData<Request>
  ) {
    try {
      if (!navigator.onLine) {
        return
      }
      const token = await getAccessTokenSilently()

      options.headers = { ...(options.headers ?? {}) }

      const hasContentType = Object.keys(options.headers).some(
        (k) => k.toLowerCase() === "content-type"
      )

      // Once upon a time all the Post requests were for pdf, but now we also need application/json
      // So unless Content-Type: application/json is specifically stated we default to /pdf
      if (!hasContentType) {
        options.headers["Content-Type"] = "application/pdf"
      }

      const data = await instance({
        ...options,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Request-ID": user?.sub || "",
          ...options.headers,
        },
      })

      return data as AxiosResponse<Response>
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          toast.warn(<ToasterLogout info={`logout due to ${options.url}`} />, {
            toastId: "logout123",
            position: "top-right",
            autoClose: false,
          })
        }
      }
      throw err
    }
  }
}
